// v84.7 — Halaman admin Live Map: semua teknisi aktif + lokasi terakhir + WO aktif.
// Auto-refresh 30 detik. Peta Leaflet + marker per teknisi.
import { useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { MapPin, Users, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { enqueueMove, fmtRouteEta, loadLeaflet, moveMarker, playPath } from "@/lib/leaflet";

const REFRESH_MS = 15000;
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const BANJARBARU_CENTER = [-3.4405, 114.8419];
const samePt = (a, b) => a && b && a[0] === b[0] && a[1] === b[1];
const fmtAge = (m) => (m == null ? "-" : m < 1 ? `${Math.max(1, Math.round(m * 60))} dtk lalu` : `${m.toFixed(0)} min lalu`);

// Titik trail yg belum diputar sejak update terakhir → diputar merata selama 1 siklus refresh.
const animateTo = (marker, trail, target) => {
  const last = marker._gacLast;
  marker._gacLast = target;
  if (!last) { marker.setLatLng(target); return; }
  if (samePt(last, target)) return;
  let idx = -1;
  for (let i = trail.length - 1; i >= 0; i--) if (samePt(trail[i], last)) { idx = i; break; }
  const pts = idx >= 0 ? trail.slice(idx + 1) : [];
  if (!pts.length || !samePt(pts[pts.length - 1], target)) pts.push(target);
  if (pts.length > 40) { moveMarker(marker, target); return; }   // backlog panjang (offline) → langsung ke posisi terbaru
  playPath(marker, pts, REFRESH_MS - 500);
};

export default function LiveMap() {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});   // tech_id → { marker, popup }
  const trailsRef = useRef({});    // tech_id → L.polyline
  const routesRef = useRef({});    // tech_id → {line, dest} garis biru Menuju Lokasi
  const leafletReadyRef = useRef(false);
  const fittedRef = useRef(false);   // auto-fit sekali saja, tidak mereset zoom admin
  const [leafletReady, setLeafletReady] = useState(false);

  // Status online realtime: waktu titik terakhir (jam browser) dari polling & SSE, dievaluasi tiap 5 dtk.
  const seenRef = useRef({});
  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 5000); return () => clearInterval(t); }, []);

  const load = async () => {
    setRefreshing(true);
    try {
      const d = await api.adminTechLocations();
      const now = Date.now();
      (d || []).forEach((t) => {
        if (!t.location) return;
        const at = now - (t.location.age_min || 0) * 60000;
        seenRef.current[t.tech_id] = Math.max(seenRef.current[t.tech_id] || 0, at);
      });
      setTechs(d || []);
    } catch (e) { /* ignore */ }
    finally { setRefreshing(false); setLoading(false); }
  };

  const pingGps = async (t) => {
    try { await api.adminGpsPing(t.tech_id); toast.success(`Ping dikirim ke ${t.tech_name}`); load(); }
    catch { toast.error("Ping gagal"); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  // Realtime: titik GPS baru langsung menggerakkan marker (tanpa menunggu refresh 15 dtk).
  useEffect(() => {
    const es = new EventSource(`${API}/wo/events`, { withCredentials: true });
    es.addEventListener("tech_loc", (e) => {
      let d;
      try { d = JSON.parse(e.data); } catch { return; }
      seenRef.current[d.tech_id] = Date.now();
      setTick((x) => x + 1);
      const m = markersRef.current[d.tech_id]?.marker;
      const target = [d.lat, d.lng];
      if (!m || samePt(m._gacLast, target)) return;
      m._gacLast = target;
      m._gacLive = Date.now();
      m.setOpacity(1);
      enqueueMove(m, target, Date.parse(d.updated_at) || Date.now());
    });
    return () => es.close();
  }, []);

  // Load Leaflet + SlideTo via CDN
  useEffect(() => {
    loadLeaflet().then((L) => {
      if (!mapContainerRef.current || mapRef.current) return;
      const map = L.map(mapContainerRef.current).setView(BANJARBARU_CENTER, 12);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap", maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      leafletReadyRef.current = true;
      setLeafletReady(true);
    });
  }, []);

  // Sync markers dgn state techs
  useEffect(() => {
    if (!leafletReadyRef.current) return;
    const L = window.L;
    const map = mapRef.current;
    if (!map || !L) return;
    const validTechs = techs.filter(t => t.location);
    // Remove markers untuk tech yg tidak ada lokasinya lagi
    Object.keys(markersRef.current).forEach(tid => {
      if (!validTechs.find(t => t.tech_id === tid)) {
        try { map.removeLayer(markersRef.current[tid].marker); } catch {}
        delete markersRef.current[tid];
      }
    });
    validTechs.forEach(t => {
      const { lat, lng, age_min, online } = t.location;
      const existing = markersRef.current[t.tech_id];
      const popupHtml = `
        <div style="min-width:180px">
          <b>${esc(t.tech_name || "-")}</b><br>
          <span style="color:${online ? "#059669" : "#999"}">${online ? "Online" : "Offline"} · update ${fmtAge(age_min)}</span>
          ${t.active_wo ? `<hr style="margin:6px 0">
            <div style="font-size:11px;color:#666">Sedang mengerjakan:</div>
            <div>${esc(t.active_wo.customer_name || "-")}</div>
            <div style="font-size:11px;color:#666">${esc(t.active_wo.service_type)} · ${esc(t.active_wo.status)}</div>
            ${t.active_wo.route ? `<div style="font-size:11px;color:#1D4ED8;font-weight:600">Menuju lokasi · tiba ${fmtRouteEta(t.active_wo.route)}</div>` : ""}
            ${t.active_wo.tracking_token ? `<a href="/track/${t.active_wo.tracking_token}" target="_blank" style="font-size:11px;color:#2962FF">Buka Tracking →</a>` : ""}
            ` : `<hr style="margin:6px 0"><div style="font-size:11px;color:#999">Belum ada WO aktif hari ini</div>`}
        </div>`;
      if (existing) {
        // Realtime aktif (event < 30 dtk) → marker digerakkan SSE; polling hanya cadangan.
        if (Date.now() - (existing.marker._gacLive || 0) > 30000) {
          animateTo(existing.marker, (t.trail || []).filter(p => Array.isArray(p) && p.length === 2), [lat, lng]);
        }
        existing.marker.setPopupContent(popupHtml);
        if (!online) existing.marker.setOpacity(0.45);
        else existing.marker.setOpacity(1);
      } else {
        // v84.134 — Custom armada icon (menggantikan balon biru default Leaflet)
        const armadaIcon = L.icon({
          iconUrl: "/armada-marker.png",
          iconSize: [56, 37],
          iconAnchor: [28, 18],
          popupAnchor: [0, -20],
        });
        const marker = L.marker([lat, lng], { icon: armadaIcon, opacity: online ? 1 : 0.45 }).addTo(map);
        marker._gacLast = [lat, lng];
        marker.bindPopup(popupHtml);
        marker.on("click", () => setSelected(t));
        markersRef.current[t.tech_id] = { marker };
      }
      // v84.8 — Trail 1 jam terakhir (garis merah)
      const trail = (t.trail || []).filter(p => Array.isArray(p) && p.length === 2);
      if (trail.length >= 2) {
        if (trailsRef.current[t.tech_id]) {
          trailsRef.current[t.tech_id].setLatLngs(trail);
        } else {
          trailsRef.current[t.tech_id] = L.polyline(trail, {
            color: "#EF4444", weight: 3, opacity: 0.7, dashArray: "6,4",
          }).addTo(map);
        }
      } else if (trailsRef.current[t.tech_id]) {
        // Trail tinggal <2 titik → hapus polyline
        try { map.removeLayer(trailsRef.current[t.tech_id]); } catch {}
        delete trailsRef.current[t.tech_id];
      }
    });
    // Garis biru rute jalan (OpenRouteService) teknisi → pelanggan saat "Menuju Lokasi"
    const routeTechs = new Set();
    validTechs.forEach(t => {
      const w = t.active_wo;
      if (!(w && w.field_status === "otw" && w.lat != null && w.lng != null)) return;
      routeTechs.add(t.tech_id);
      const pts = w.route?.coordinates?.length >= 2 ? w.route.coordinates : [[t.location.lat, t.location.lng], [w.lat, w.lng]];
      const r = routesRef.current[t.tech_id];
      if (r) { r.line.setLatLngs(pts); r.dest.setLatLng([w.lat, w.lng]); return; }
      routesRef.current[t.tech_id] = {
        line: L.polyline(pts, { color: "#2563EB", weight: 5, opacity: 0.85 }).addTo(map),
        dest: L.circleMarker([w.lat, w.lng], { radius: 7, color: "#1D4ED8", fillColor: "#3B82F6", fillOpacity: 1 })
          .bindTooltip(esc(w.customer_name || "Pelanggan")).addTo(map),
      };
    });
    Object.keys(routesRef.current).forEach(tid => {
      if (routeTechs.has(tid)) return;
      try { map.removeLayer(routesRef.current[tid].line); map.removeLayer(routesRef.current[tid].dest); } catch {}
      delete routesRef.current[tid];
    });
    // Bersihkan trail untuk tech yg tidak ada lagi
    Object.keys(trailsRef.current).forEach(tid => {
      if (!validTechs.find(t => t.tech_id === tid)) {
        try { map.removeLayer(trailsRef.current[tid]); } catch {}
        delete trailsRef.current[tid];
      }
    });
    // Auto-fit bounds kalau ≥2 tech ada lokasi
    if (fittedRef.current || !validTechs.length) return;
    fittedRef.current = true;
    if (validTechs.length >= 2) {
      const latlngs = validTechs.map(t => [t.location.lat, t.location.lng]);
      try { map.fitBounds(latlngs, { padding: [50, 50], maxZoom: 14 }); } catch {}
    } else if (validTechs.length === 1) {
      map.setView([validTechs[0].location.lat, validTechs[0].location.lng], 13);
    }
  }, [techs, leafletReady]);

  const liveTechs = useMemo(() => techs.map((t) => {
    if (!t.location) return t;
    const ageS = (Date.now() - (seenRef.current[t.tech_id] || 0)) / 1000;
    const online = ageS < (t.location.online_window_s || 40);
    return { ...t, location: { ...t.location, age_min: ageS / 60, online } };
  }), [techs, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    liveTechs.forEach((t) => markersRef.current[t.tech_id]?.marker?.setOpacity(t.location?.online ? 1 : 0.45));
  }, [liveTechs]);
  const withLoc = useMemo(() => liveTechs.filter(t => t.location?.online), [liveTechs]);
  const noLoc = useMemo(() => techs.filter(t => !t.location), [techs]);

  return (
    <div className="p-4 space-y-4" data-testid="live-map-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Dashboard</div>
          <div className="text-[18px] font-bold text-zinc-800 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-600" />
            Live Map Armada
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            Auto-refresh tiap 15 detik. Sumber GPS: aplikasi HP (APP, diutamakan) atau Traccar. Garis biru = teknisi sedang menuju lokasi pelanggan.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={refreshing}
          className="gap-1.5 rounded-sm" data-testid="live-map-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4">
        <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
          <div ref={mapContainerRef} style={{ height: "560px", width: "100%" }} data-testid="live-map" />
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border bg-white shadow-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Teknisi Aktif Saat Ini
            </div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <span className="text-[28px] font-bold text-emerald-600" data-testid="live-map-online-count">
                {withLoc.length}
              </span>
              <span className="text-[13px] text-zinc-500">dari {techs.length} armada</span>
            </div>
          </div>

          <div className="rounded-xl border bg-white shadow-sm">
            <div className="px-3 py-2 border-b text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Daftar Teknisi ({techs.length})
            </div>
            <div className="max-h-[420px] overflow-auto">
              {loading && <div className="p-3 text-[12px] text-zinc-500">Memuat…</div>}
              {!loading && liveTechs.map(t => (
                <div key={t.tech_id}
                  className={`px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${
                    selected?.tech_id === t.tech_id ? "bg-emerald-50" : ""
                  }`}
                  onClick={() => {
                    setSelected(t);
                    if (t.location && mapRef.current) {
                      mapRef.current.setView([t.location.lat, t.location.lng], 15);
                      markersRef.current[t.tech_id]?.marker?.openPopup();
                    }
                  }}
                  data-testid={`live-map-tech-${t.tech_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-zinc-800 truncate">{t.tech_name}</div>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                        {t.location ? (
                          <>
                            <span className={`w-1.5 h-1.5 rounded-full ${t.location.online ? "bg-emerald-500" : "bg-zinc-400"}`}
                              data-testid={`live-map-online-dot-${t.tech_id}`} />
                            <span data-testid={`live-map-status-${t.tech_id}`}>
                              {t.location.online ? "" : "Offline · "}{fmtAge(t.location.age_min)}
                            </span>
                            <span className={`px-1 rounded-sm text-[9px] font-semibold ${t.location.source === "mobile" ? "bg-blue-100 text-blue-700" : "bg-zinc-100 text-zinc-600"}`}
                              data-testid={`live-map-source-${t.tech_id}`}>
                              {t.location.source === "mobile" ? "APP" : "TRACCAR"}
                            </span>
                          </>
                        ) : (
                          <>
                            <WifiOff className="w-2.5 h-2.5 text-zinc-400" />
                            <span>GPS tidak aktif</span>
                          </>
                        )}
                        <span className="text-zinc-400">·</span>
                        <span>{t.wo_count_today} WO hari ini</span>
                      </div>
                      {t.active_wo && (
                        <div className="text-[10px] text-zinc-600 truncate mt-0.5">
                          → {t.active_wo.customer_name}
                        </div>
                      )}
                      {t.active_wo?.route && (
                        <div className="text-[12px] text-blue-700 font-semibold mt-0.5" data-testid={`live-map-eta-${t.tech_id}`}>
                          Menuju lokasi · tiba {fmtRouteEta(t.active_wo.route)}
                        </div>
                      )}
                    </div>
                    {t.uses_mobile_app && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); pingGps(t); }}
                        className="text-[10px] text-blue-600 hover:underline flex-none"
                        data-testid={`live-map-ping-${t.tech_id}`}>
                        {t.ping_pending ? "Ping…" : "Ping GPS"}
                      </button>
                    )}
                    {t.active_wo?.tracking_token && (
                      <Link to={`/track/${t.active_wo.tracking_token}`} target="_blank"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[10px] text-[#2962FF] hover:underline flex-none">
                        Track
                      </Link>
                    )}
                  </div>
                </div>
              ))}
              {!loading && !techs.length && (
                <div className="p-6 text-center text-[12px] text-zinc-500">
                  Belum ada teknisi aktif.
                </div>
              )}
            </div>
          </div>

          {!!noLoc.length && (
            <div className="rounded-xl border bg-amber-50 border-amber-200 p-3 text-[11px] text-amber-900">
              💡 <b>{noLoc.length} teknisi</b> belum aktif GPS-nya. Share link{" "}
              <span className="font-mono text-[10px]">/tech/gps?token=…</span> ke mereka dari halaman Data Teknisi.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
